import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { RefreshCw, CheckCircle, AlertCircle, Loader2, CreditCard } from 'lucide-react';

const StripeSyncPage: React.FC = () => {
  // Fetch Stripe sync status
  const { data: syncStatus, isLoading, refetch } = useQuery({
    queryKey: ['stripe-sync-status'],
    queryFn: async () => {
      const response = await api.get('/admin/stripe/stripe-status');
      return response.data;
    },
  });

  // Sync tiers mutation
  const syncTiersMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/admin/stripe/sync-tiers');
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Synced ${data.synced} tiers successfully!`);
      refetch();
    },
    onError: (error: any) => {
      toast.error(`Failed to sync tiers: ${error.response?.data?.detail || error.message}`);
    },
  });

  // Sync credit packages mutation
  const syncPackagesMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/admin/stripe/sync-credit-packages');
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Synced ${data.synced} credit packages successfully!`);
      refetch();
    },
    onError: (error: any) => {
      toast.error(`Failed to sync credit packages: ${error.response?.data?.detail || error.message}`);
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-black dark:text-white">Stripe Sync</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Sync tiers and credit packages with Stripe products and prices
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          <span>Refresh Status</span>
        </Button>
      </div>

      {/* Sync Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sync Tiers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6"
        >
          <div className="flex items-center space-x-3 mb-4">
            <CreditCard className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-black dark:text-white">Subscription Tiers</h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Synced: <span className="font-semibold">{syncStatus?.tiers_synced || 0}</span> / {syncStatus?.tiers_total || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            Creates Stripe products and prices based on tier data in your database
          </p>
          <Button
            onClick={() => syncTiersMutation.mutate()}
            disabled={syncTiersMutation.isPending}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white mt-4"
          >
            {syncTiersMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Sync Tiers with Stripe
              </>
            )}
          </Button>
        </motion.div>

        {/* Sync Credit Packages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6"
        >
          <div className="flex items-center space-x-3 mb-4">
            <CreditCard className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-semibold text-black dark:text-white">Credit Packages</h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Synced: <span className="font-semibold">{syncStatus?.packages_synced || 0}</span> / {syncStatus?.packages_total || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            Creates Stripe products and prices for credit packages
          </p>
          <Button
            onClick={() => syncPackagesMutation.mutate()}
            disabled={syncPackagesMutation.isPending}
            className="w-full bg-green-600 hover:bg-green-700 text-white mt-4"
          >
            {syncPackagesMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Sync Credit Packages
              </>
            )}
          </Button>
        </motion.div>
      </div>

      {/* Tiers Sync Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6"
      >
        <h2 className="text-xl font-semibold text-black dark:text-white mb-4">Tiers Sync Status</h2>
        <div className="space-y-3">
          {syncStatus?.tiers?.map((tier: any) => (
            <div
              key={tier.name}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <div className="flex-1">
                <p className="font-medium text-black dark:text-white">{tier.display_name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Monthly: €{tier.price_monthly} | Yearly: €{tier.price_yearly}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {tier.has_stripe_product && tier.has_monthly_price ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="w-5 h-5 mr-1" />
                    <span className="text-sm font-medium">Synced</span>
                  </div>
                ) : (
                  <div className="flex items-center text-orange-600">
                    <AlertCircle className="w-5 h-5 mr-1" />
                    <span className="text-sm font-medium">Not Synced</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Credit Packages Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6"
      >
        <h2 className="text-xl font-semibold text-black dark:text-white mb-4">Credit Packages Status</h2>
        <div className="space-y-3">
          {syncStatus?.packages?.map((pkg: any) => (
            <div
              key={pkg.name}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <div className="flex-1">
                <p className="font-medium text-black dark:text-white">{pkg.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {pkg.credits} credits - €{pkg.price}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {pkg.has_stripe_product && pkg.has_stripe_price ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="w-5 h-5 mr-1" />
                    <span className="text-sm font-medium">Synced</span>
                  </div>
                ) : (
                  <div className="flex items-center text-orange-600">
                    <AlertCircle className="w-5 h-5 mr-1" />
                    <span className="text-sm font-medium">Not Synced</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default StripeSyncPage;
